/**
 * NotificationsPanel — bell dropdown showing the pending-notifications
 * feed sourced from `useNotificationsFeed`. Clicking an item marks it
 * read and navigates to its target.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, FileText, ShieldCheck, UserCog, Check, Sparkles, RotateCcw, Gavel } from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import { loadWallet } from "@/lib/wallet-storage";
import { sendDemoDecision, resetDemoDecisions } from "@/lib/wallet-decisions";

const KIND_ICON = {
  inbox:    FileText,
  passport: ShieldCheck,
  profile:  UserCog,
  decision: Gavel,
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60)     return "just now";
  if (s < 3600)   return `${Math.floor(s / 60)} min ago`;
  if (s < 86400)  return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

export default function NotificationsPanel({ open, onClose, items, markRead, markAllRead }) {
  const ref = useRef(null);
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);

  async function handleDemoSend() {
    const w = loadWallet() || {};
    if (!w.device_id || busy) return;
    setBusy(true);
    try {
      const d = await sendDemoDecision(w.device_id);
      onClose();
      nav(`/wallet/decisions/${d.decision_id}`);
    } finally { setBusy(false); }
  }
  async function handleDemoReset() {
    const w = loadWallet() || {};
    if (!w.device_id || busy) return;
    if (!window.confirm("Reset every demo decision on this device?")) return;
    setBusy(true);
    try { await resetDemoDecisions(w.device_id); }
    finally { setBusy(false); }
  }

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) onClose(); };
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      ref={ref}
      data-testid="notifications-panel"
      className="absolute right-0 top-11 z-30 w-[calc(100vw-32px)] max-w-sm rounded-2xl bg-white shadow-2xl border border-slate-100 overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4" style={{ color: NAVY }} />
          <span className="text-[13px] font-semibold" style={{ color: NAVY }}>Notifications</span>
        </div>
        {items.some(i => i.unread) && (
          <button
            onClick={markAllRead}
            data-testid="notifications-mark-all"
            className="text-[11px] font-medium hover:underline"
            style={{ color: NAVY }}
          >
            Mark all read
          </button>
        )}
      </div>
      <div className="max-h-[60vh] overflow-y-auto">
        {items.length === 0 ? (
          <div className="p-8 text-center">
            <div className="w-11 h-11 mx-auto rounded-full flex items-center justify-center mb-2" style={{ background: "#F1F5F9" }}>
              <Check className="w-5 h-5 text-slate-400" />
            </div>
            <div className="text-[13px] font-medium" style={{ color: NAVY }}>You're all caught up</div>
            <div className="text-[11px] text-slate-500 mt-0.5">Nothing needs your attention right now.</div>
          </div>
        ) : (
          items.map((it) => {
            const Icon = KIND_ICON[it.kind] || Bell;
            return (
              <Link
                key={it.id}
                to={it.to}
                data-testid={`notification-${it.id}`}
                onClick={() => { markRead(it.id); onClose(); }}
                className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition border-b border-slate-100 last:border-0"
              >
                <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: it.unread ? `${GOLD}33` : "#F1F5F9" }}>
                  <Icon className="w-3.5 h-3.5" style={{ color: NAVY }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold leading-snug" style={{ color: NAVY }}>{it.title}</div>
                  <div className="text-[11.5px] text-slate-600 mt-0.5 leading-snug">{it.detail}</div>
                  {it.when && <div className="text-[10.5px] text-slate-400 mt-1">{relTime(it.when)}</div>}
                </div>
                {it.unread && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ background: GOLD }} aria-label="Unread" />}
              </Link>
            );
          })
        )}
      </div>
      {/* Demo tools footer */}
      <div className="border-t border-slate-100 bg-slate-50 px-3 py-2 flex items-center justify-between gap-2 flex-wrap" data-testid="notifications-demo-tools">
        <div className="text-[10.5px] text-slate-500">Demo tools</div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={handleDemoSend}
            disabled={busy}
            data-testid="notifications-demo-send"
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
            style={{ color: NAVY }}
          >
            <Sparkles className="w-3 h-3" style={{ color: GOLD }} /> Send demo proposal
          </button>
          <button
            onClick={handleDemoReset}
            disabled={busy}
            data-testid="notifications-demo-reset"
            className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
            style={{ color: "#B91C1C" }}
          >
            <RotateCcw className="w-3 h-3" /> Reset
          </button>
        </div>
      </div>
    </div>
  );
}
